import { CheckCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Empty, Form, Select, Space, Spin, Tag, Timeline, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import packageInfo from '../../package.json';
import { getReleaseNotes } from '../services/releaseNotesService';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

const sortVersionsDesc = (items = []) =>
    [...items].sort((a, b) =>
        b.version.localeCompare(a.version, undefined, { numeric: true, sensitivity: 'base' })
    );

const ReleaseNotesPage = () => {
    const [form] = Form.useForm();
    const [notes, setNotes] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const sortedVersions = useMemo(
        () => sortVersionsDesc(notes),
        [notes]
    );

    const selectedNote = useMemo(
        () => notes.find((note) => note.version === selectedVersion) || null,
        [notes, selectedVersion]
    );

    const loadReleaseNotes = async () => {
        setLoading(true);
        setError('');
        try {
            const loadedNotes = await getReleaseNotes();
            const orderedNotes = sortVersionsDesc(loadedNotes);
            const nextVersion = orderedNotes[0]?.version;

            setNotes(loadedNotes);
            setSelectedVersion(nextVersion);
            form.setFieldsValue({ version: nextVersion });
        } catch (fetchError) {
            setError(fetchError.message || 'No se pudieron cargar las notas de versión.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadReleaseNotes();
    }, []);

    const renderContent = () => {
        if (loading) {
            return (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Spin size="large" />
                </div>
            );
        }

        if (!selectedNote) {
            return <Empty description="No hay cambios registrados para esta versión." />;
        }

        return (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Space wrap>
                    <Title level={4} style={{ margin: 0 }}>
                        Versión v{selectedNote.version}
                    </Title>
                    {selectedNote.version === packageInfo.version && (
                        <Tag color="green">Versión actual</Tag>
                    )}
                </Space>

                <Timeline
                    items={selectedNote.changes.map((change) => ({
                        dot: <CheckCircleOutlined style={{ color: '#1677ff' }} />,
                        children: (
                            <div>
                                {change.type && <Text strong>{change.type}</Text>}
                                <Paragraph style={{ margin: change.type ? '4px 0 0 0' : 0 }}>
                                    {change.description || change}
                                </Paragraph>
                            </div>
                        ),
                    }))}
                />
            </Space>
        );
    };

    return (
        <div style={{ padding: '24px' }}>
            <Card title="Notas de la versión" style={{ maxWidth: 900, margin: '0 auto' }}>
                <Paragraph style={{ marginBottom: 20 }}>
                    Consulta los cambios publicados por versión.
                </Paragraph>

                {error && (
                    <Alert
                        type="warning"
                        showIcon
                        message="No fue posible cargar las release notes"
                        description={error}
                        action={
                            <Button size="small" onClick={loadReleaseNotes}>
                                Reintentar
                            </Button>
                        }
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Form form={form} layout="vertical" style={{ marginBottom: 24 }}>
                    <Form.Item
                        label="Selecciona una versión"
                        name="version"
                        initialValue={selectedVersion}
                    >
                        <Select
                            placeholder="Selecciona una versión"
                            onChange={setSelectedVersion}
                            allowClear={false}
                            disabled={loading || sortedVersions.length === 0}
                        >
                            {sortedVersions.map((note) => (
                                <Option key={note.version} value={note.version}>
                                    v{note.version}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>

                {renderContent()}

            </Card>
        </div>
    );
};

export default ReleaseNotesPage;
